import {EditorState, type Extension, MapMode, RangeSetBuilder, StateEffect, StateField} from '@codemirror/state'
import {Decoration, type DecorationSet, EditorView} from '@codemirror/view'

import {getNewlinePrefix, getNewlineSuffix} from '../../language-tools/source-utils'
import {UploadPlaceholderWidget, type UploadPlaceholderWidgetProps} from '../widgets/upload-placeholder'
import {activeSelectionChanged, getActiveSelection} from './active-nodes'
import {createText} from './single-line-editor'

interface UploadPlaceholderSpec {
  id: string
  /** Offset of the placeholder in the document. */
  position: number
  data: UploadPlaceholderWidgetProps
}

interface UploadPlaceholderUpdate {
  id: string
  data: Partial<UploadPlaceholderWidgetProps>
}

type PlaceholderEntry = Omit<UploadPlaceholderSpec, 'id'>

interface PlaceholderFieldState {
  entries: ReadonlyMap<string, PlaceholderEntry>
  decorations: DecorationSet
}

const addUploadPlaceholderEffect = StateEffect.define<UploadPlaceholderSpec>()

const updateUploadPlaceholderEffect = StateEffect.define<UploadPlaceholderUpdate>()

const removeUploadPlaceholderEffect = StateEffect.define<{id: string}>()

/**
 * Get the doc as a string, but insert a character at all existing placeholder widgets so that the newline
 * prefix/suffix calculations consider the widgets as content, even though there's not yet any content there.
 */
function buildDocWithPlaceholderChars(
  view: EditorView,
  caretPosition: number,
): [doc: string, adjustedCaretPosition: number] {
  const widgets = getAllEntries(view.state)
  let doc = view.state.doc.toString()
  let adjustedCaretPosition = caretPosition

  const placeholderChar = 'x'

  for (const {position} of widgets) {
    const before = doc.slice(0, position)
    const after = doc.slice(position)
    doc = before + placeholderChar + after
    if (adjustedCaretPosition > position) adjustedCaretPosition += 1
  }

  return [doc, adjustedCaretPosition]
}

export function addUploadPlaceholders(
  view: EditorView,
  position: number,
  ...specs: Array<Omit<UploadPlaceholderSpec, 'position'>>
) {
  // adjust the doc and position to consider any existing pending uploads when inserting newlines
  const [doc, adjustedPosition] = buildDocWithPlaceholderChars(view, position)

  let insertedNewlines = ''

  // Insert newlines before first and between each item, adjusting positions accordingly
  const effects = specs.map((spec, i) => {
    insertedNewlines += i === 0 ? getNewlinePrefix(doc, adjustedPosition) : '\n\n'
    return addUploadPlaceholderEffect.of({...spec, position: position + insertedNewlines.length})
  })

  const prependedNewlinesLength = insertedNewlines.length

  insertedNewlines += getNewlineSuffix(doc, adjustedPosition)

  view.dispatch({
    effects,
    changes: {from: position, insert: createText(insertedNewlines)},
    // Move caret down to line after the last item, so all items can show preview (not be active)
    selection: {anchor: position + prependedNewlinesLength + 2},
    scrollIntoView: true,
  })
}

export function updateUploadPlaceholder(view: EditorView, update: UploadPlaceholderUpdate) {
  view.dispatch({effects: [updateUploadPlaceholderEffect.of(update)]})
}

function isActive(state: EditorState, position: number) {
  const selection = getActiveSelection(state)
  return selection?.ranges.some(range => range.to >= position && range.from <= position) ?? false
}

/** Remove the placeholder from the view and replace with the `insert` text. Has no effect if the placeholder has been deleted. */
export function replaceUploadPlaceholder(view: EditorView, id: string, insert: string) {
  const position = view.state.field(uploadPlaceholderField).entries.get(id)?.position ?? null
  // location will be null if the placeholder has been deleted
  if (position === null) return

  // Note: we intentionally do not scroll into view in this case; the upload may complete outside of the current view
  // and scrolling would be disruptive
  view.dispatch({
    effects: [removeUploadPlaceholderEffect.of({id})],
    changes: {from: position, insert: createText(insert)},
  })
}

export function getAllEntries(state: EditorState): UploadPlaceholderSpec[] {
  const field = state.field(uploadPlaceholderField)
  return Array.from(field.entries.entries()).map(([id, entry]) => ({
    id,
    ...entry,
  }))
}

const uploadPlaceholderField = StateField.define<PlaceholderFieldState>({
  create: () => ({entries: new Map(), decorations: Decoration.none}),
  update: (state, tr) => {
    const entries = new Map(state.entries)
    let changed = false

    let skipMapping = false

    for (const effect of tr.effects) {
      if (effect.is(addUploadPlaceholderEffect)) {
        const {id, ...entry} = effect.value
        entries.set(id, entry)
        changed = true
        skipMapping = true // we already handle the mapping for this case when adding the placeholder
      } else if (effect.is(updateUploadPlaceholderEffect) && entries.has(effect.value.id)) {
        const existing = entries.get(effect.value.id)!
        entries.set(effect.value.id, {
          ...existing,
          data: {...existing.data, ...effect.value.data},
        })
        changed = true
      } else if (effect.is(removeUploadPlaceholderEffect)) {
        changed = entries.delete(effect.value.id)
      }
    }

    if (tr.docChanged && entries.size > 0 && !skipMapping) {
      for (const [id, entry] of entries) {
        const newPosition = tr.changes.mapPos(entry.position, 1, MapMode.TrackAfter)

        if (newPosition !== entry.position) {
          // If the location is `null`, the placeholder has been deleted and so we forget about it
          if (newPosition === null) {
            entries.delete(id)
          } else {
            entries.set(id, {
              ...entry,
              position: newPosition,
            })
          }

          changed = true
        }
      }
    }

    if (activeSelectionChanged(tr)) {
      for (const [id, entry] of entries) {
        const newActive = isActive(tr.state, entry.position)

        if (newActive !== entry.data.active) {
          entries.set(id, {
            ...entry,
            data: {...entry.data, active: newActive},
          })
          changed = true
        }
      }
    }

    if (!changed) return state

    // Rebuild decorations from scratch for all entries
    const builder = new RangeSetBuilder<Decoration>()
    const sortedEntries = Array.from(entries).sort(([, a], [, b]) => {
      if (a.position !== b.position) return a.position - b.position
      return 0
    })

    for (const [id, entry] of sortedEntries) {
      const decoration = Decoration.replace({
        widget: new UploadPlaceholderWidget(id, entry.data),
        block: true,
      })
      builder.add(entry.position, entry.position + 1, decoration)
    }
    const decorations = builder.finish()

    return {
      entries,
      decorations,
    }
  },
  provide: field => EditorView.decorations.from(field, value => value.decorations),
})

export const uploadPlaceholderExtension: Extension = [uploadPlaceholderField]
